import { createReducer } from '@reduxjs/toolkit';
import {
  fetchQueues,
  savePriorityOrder,
  reorderQueues,
  reorderPinnedQueues,
  togglePin,
  revertOrder,
  setQueueCoderCount,
  QueueItem
} from '../actions/queue-priority-order.action';

export interface QueuePriorityOrderState {
  queues: QueueItem[];
  originalOrder: string[];
  currentOrder: string[];
  originalPinnedOrder: string[];
  currentPinnedOrder: string[];
  hasExplicitPriority: boolean;
  originalHasExplicitPriority: boolean;
  // Whether the user has dragged a card since the last fetch. Not the same as
  // hasExplicitPriority, which the backend reports and which can already be true from a
  // save made days ago. Only an unsaved drag may move the lastUserOrderAt timestamp.
  hasUserReordered: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  lastAutoResetAt: string | null;
}

const initialState: QueuePriorityOrderState = {
  queues: [],
  originalOrder: [],
  currentOrder: [],
  originalPinnedOrder: [],
  currentPinnedOrder: [],
  hasExplicitPriority: false,
  originalHasExplicitPriority: false,
  hasUserReordered: false,
  isLoading: false,
  isSaving: false,
  error: null,
  saveError: null,
  lastAutoResetAt: null
};

const move = (order: string[], fromIndex: number, toIndex: number): string[] => {
  const len = order.length;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= len || toIndex >= len || fromIndex === toIndex) {
    return order;
  }
  const newOrder = [...order];
  const [moved] = newOrder.splice(fromIndex, 1);
  newOrder.splice(toIndex, 0, moved);
  return newOrder;
};

// Where to re-insert queueId into targetList when moving it back from the other section.
// If queueId still has a remembered slot in referenceOrder (the last saved order for the
// section it's returning to), reinsert at the position implied by its still-present
// neighbors there — this makes a pin→unpin round trip with no other edits a true no-op.
// Otherwise (its old slot was never saved, or was discarded by a prior save) fall back.
const restoreIndex = (
  queueId: string,
  targetList: string[],
  referenceOrder: string[],
  fallbackIndex: number
): number => {
  const posInReference = referenceOrder.indexOf(queueId);
  if (posInReference === -1) {
    return fallbackIndex;
  }
  let index = 0;
  for (let i = 0; i < posInReference; i++) {
    if (targetList.includes(referenceOrder[i])) {
      index++;
    }
  }
  return index;
};

const insertAt = (list: string[], index: number, id: string): string[] => [
  ...list.slice(0, index),
  id,
  ...list.slice(index)
];

const reducer = createReducer(initialState, builder => {
  builder.addCase(fetchQueues.pending, state => {
    state.isLoading = true;
    state.error = null;
  });

  builder.addCase(fetchQueues.fulfilled, (state, action) => {
    const queues = Array.isArray(action.payload?.queues) ? action.payload.queues : [];
    const pinned = queues
      .filter(q => q.isPinned)
      .sort((a, b) => (a.pinOrder ?? Infinity) - (b.pinOrder ?? Infinity));
    const pinnedIds = pinned.map(q => q.id);
    const pinnedIdSet = new Set(pinnedIds);
    // isPinned alone decides the section. isExplicitlyRanked only says whether a user or
    // the backend's fallback cascade chose the position, so it neither hides a queue here
    // nor changes a sort. filter() copies first, so state.queues keeps the response order.
    const unpinnedIds = queues
      .filter(q => !pinnedIdSet.has(q.id))
      .sort((a, b) => (a.priorityRank ?? Infinity) - (b.priorityRank ?? Infinity))
      .map(q => q.id);

    state.queues = queues;
    state.originalOrder = unpinnedIds;
    state.currentOrder = unpinnedIds;
    state.originalPinnedOrder = pinnedIds;
    state.currentPinnedOrder = pinnedIds;
    state.hasExplicitPriority = action.payload?.hasExplicitPriority ?? false;
    state.originalHasExplicitPriority = state.hasExplicitPriority;
    // A fresh fetch has no unsaved drag.
    state.hasUserReordered = false;
    state.lastAutoResetAt = action.payload?.lastAutoResetAt ?? null;
    state.isLoading = false;
  });

  builder.addCase(fetchQueues.rejected, (state, action) => {
    state.isLoading = false;
    state.error = action.error.message ?? 'Failed to load queues';
  });

  builder.addCase(reorderQueues, (state, action) => {
    const { fromIndex, toIndex } = action.payload;
    const reordered = move(state.currentOrder, fromIndex, toIndex);
    if (reordered !== state.currentOrder) {
      state.currentOrder = reordered;
      // Dragging an unpinned queue is the user explicitly setting its priority.
      state.hasExplicitPriority = true;
      state.hasUserReordered = true;
    }
  });

  builder.addCase(reorderPinnedQueues, (state, action) => {
    const { fromIndex, toIndex } = action.payload;
    const reordered = move(state.currentPinnedOrder, fromIndex, toIndex);
    if (reordered !== state.currentPinnedOrder) {
      state.currentPinnedOrder = reordered;
      state.hasUserReordered = true;
    }
  });

  builder.addCase(togglePin, (state, action) => {
    const { queueId } = action.payload;
    if (state.currentPinnedOrder.includes(queueId)) {
      state.currentPinnedOrder = state.currentPinnedOrder.filter(id => id !== queueId);
      // No remembered slot (e.g. it was saved as pinned, so its old priority position was
      // discarded) → surface it at the top rather than silently burying it at the bottom.
      const insertIndex = restoreIndex(queueId, state.currentOrder, state.originalOrder, 0);
      state.currentOrder = insertAt(state.currentOrder, insertIndex, queueId);
    } else if (state.currentOrder.includes(queueId)) {
      state.currentOrder = state.currentOrder.filter(id => id !== queueId);
      const insertIndex = restoreIndex(
        queueId,
        state.currentPinnedOrder,
        state.originalPinnedOrder,
        state.currentPinnedOrder.length
      );
      state.currentPinnedOrder = insertAt(state.currentPinnedOrder, insertIndex, queueId);
    }
  });

  builder.addCase(revertOrder, state => {
    state.currentOrder = [...state.originalOrder];
    state.currentPinnedOrder = [...state.originalPinnedOrder];
    state.hasExplicitPriority = state.originalHasExplicitPriority;
    state.hasUserReordered = false;
  });

  builder.addCase(setQueueCoderCount, (state, action) => {
    const { queueId, coderCount } = action.payload;
    const queue = state.queues.find(q => q.id === queueId);
    if (queue) {
      queue.coderCount = coderCount;
    }
  });

  builder.addCase(savePriorityOrder.pending, state => {
    state.isSaving = true;
    state.saveError = null;
  });

  builder.addCase(savePriorityOrder.fulfilled, (state, action) => {
    state.isSaving = false;
    state.originalOrder = action.payload.orderedIds;
    state.originalPinnedOrder = action.payload.pinnedIds;
    state.originalHasExplicitPriority = state.hasExplicitPriority;
  });

  builder.addCase(savePriorityOrder.rejected, (state, action) => {
    state.isSaving = false;
    state.saveError = action.error.message ?? 'Failed to save priority order';
  });
});

export const selectQueuePriorityOrder = (state: any): QueuePriorityOrderState =>
  state.queuePriorityOrder;

export const selectHasUnsavedPriorityOrder = (state: any): boolean => {
  const slice: QueuePriorityOrderState | undefined = state?.queuePriorityOrder;
  if (!slice) return false;
  return (
    (slice.currentOrder ?? []).join(',') !== (slice.originalOrder ?? []).join(',')
  );
};

export default reducer;
